import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import client from "../../api/client";

const TYPE_LABELS = { 2: "Student", 4: "NYSC" };
const TYPE_COLORS = {
  2: "bg-blue-100 text-blue-700",
  4: "bg-green-100 text-green-700",
};

export default function StudentsPage() {
  const [students, setStudents] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [userType, setUserType] = useState("");
  const [aiFilter, setAiFilter] = useState("");
  const [perPage, setPerPage] = useState("50");
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const load = (p = 1) => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    setLoading(true);
    client.get("/admin/students", { params: { search, user_type: userType, ai_access: aiFilter, per_page: perPage, page: p } })
      .then(res => { setStudents(res.data.data); setMeta(res.data); setPage(p); })
      .catch(() => toast.error("Failed to load students."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(1); }, [search, userType, aiFilter, perPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // AI coach + career advisor: only students switched on here can use it while it's being tested.
  const toggleAi = async (s) => {
    const next = !s.ai_access;
    setStudents((list) => list.map((x) => (x.id === s.id ? { ...x, ai_access: next } : x)));
    try {
      await client.put(`/admin/students/${s.id}/ai-access`, { ai_access: next });
      toast.success(`AI ${next ? "switched on" : "switched off"} for ${s.name}.`);
    } catch {
      setStudents((list) => list.map((x) => (x.id === s.id ? { ...x, ai_access: !next } : x)));
      toast.error("Couldn't change AI access.");
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await client.get("/admin/students/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "students.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
    finally { setExporting(false); }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Students</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {meta?.total != null ? `${meta.total.toLocaleString()} total` : "All enrolled students"}
          </p>
        </div>
        <div className="flex gap-3 flex-wrap items-center">
          <select value={userType} onChange={e => setUserType(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 bg-white">
            <option value="">All Types</option>
            <option value="2">Student</option>
            <option value="4">NYSC</option>
          </select>

          <select value={aiFilter} onChange={e => setAiFilter(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 bg-white">
            <option value="">AI access: all</option>
            <option value="1">AI on</option>
            <option value="0">AI off</option>
          </select>

          <select value={perPage} onChange={e => setPerPage(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 bg-white">
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
            <option value="all">All</option>
          </select>

          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or email…"
            className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 w-52" />

          <button onClick={exportCsv} disabled={exporting}
            className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors disabled:opacity-50">
            {exporting ? "Exporting…" : "⬇ Export CSV"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
        {loading ? (
          <div className="p-8 space-y-3">{[1,2,3,4,5].map(i => <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : students.length === 0 ? (
          <div className="p-16 text-center"><p className="text-4xl mb-3">👩‍🎓</p><p className="text-sm font-medium text-gray-600">No students found</p></div>
        ) : (
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>{["Name", "Email", "Phone", "Type", "Courses", "Joined", "AI access"].map(h => (
                <th key={h} className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {students.map(s => (
                <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-800">{s.name}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{s.email}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{s.telephone || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${TYPE_COLORS[s.user_type] || "bg-gray-100 text-gray-500"}`}>
                      {TYPE_LABELS[s.user_type] || s.user_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs max-w-[220px] truncate">{s.courses?.join(", ") || "—"}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3">
                    <button type="button" role="switch" aria-checked={!!s.ai_access} aria-label={`AI access for ${s.name}`} onClick={() => toggleAi(s)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${s.ai_access ? "bg-[#294a93]" : "bg-gray-200"}`}>
                      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${s.ai_access ? "translate-x-4" : "translate-x-0.5"}`} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {meta && meta.last_page > 1 && perPage !== "all" && (
        <div className="flex justify-center items-center gap-2 mt-6">
          <button disabled={page === 1} onClick={() => load(page - 1)}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium disabled:opacity-40 hover:bg-gray-50 transition-colors">← Prev</button>
          <span className="px-4 py-2 text-sm text-gray-500">Page {page} of {meta.last_page}</span>
          <button disabled={page === meta.last_page} onClick={() => load(page + 1)}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium disabled:opacity-40 hover:bg-gray-50 transition-colors">Next →</button>
        </div>
      )}
    </div>
  );
}
